import React, { useEffect, useState } from 'react';

// CLAUDE-SYNC: v1.7.12 (2026-09-02) — PWA install prompt for ReyaGoWeb.
// Chrome/Edge/Android fire `beforeinstallprompt`, which we capture and
// replay from our own banner (browsers suppress their native mini-infobar
// once you call preventDefault(), so without this the install option would
// be buried in the browser menu where field staff would never find it).
// iOS Safari never fires beforeinstallprompt at all — there's no
// programmatic install API — so on iOS we instead show static "Add to Home
// Screen" instructions the one time it's relevant.
// Dismissal is remembered in localStorage so this doesn't nag on every visit.

const DISMISS_KEY = 'reyago_install_prompt_dismissed_v1';

function isIos() {
  return /iphone|ipad|ipod/i.test(window.navigator.userAgent);
}

function isStandalone() {
  return (
    window.matchMedia?.('(display-mode: standalone)')?.matches ||
    window.navigator.standalone === true // iOS Safari
  );
}

export default function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [showIosHelp, setShowIosHelp] = useState(false);

  useEffect(() => {
    if (isStandalone()) return; // already installed / running as an app
    if (localStorage.getItem(DISMISS_KEY)) return;

    if (isIos()) {
      // No install event to wait for on iOS — just offer the instructions.
      setShowIosHelp(true);
      setVisible(true);
      return;
    }

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setVisible(true);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, '1');
    setVisible(false);
  };

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    try {
      await deferredPrompt.userChoice;
    } finally {
      setDeferredPrompt(null);
      dismiss();
    }
  };

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-label="Install ReyaGo"
      style={{
        position: 'fixed',
        left: 12,
        right: 12,
        bottom: 76, // clears BottomNav
        zIndex: 500,
        background: '#4A2D4F',
        color: 'white',
        borderRadius: 12,
        padding: '12px 14px',
        boxShadow: '0 4px 16px rgba(0,0,0,0.25)',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
      }}
    >
      <div style={{ flex: 1, fontSize: 13, lineHeight: 1.4 }}>
        {showIosHelp ? (
          <>
            <strong>Install ReyaGo</strong> — tap the Share icon, then "Add to Home Screen" for faster, full-screen access.
          </>
        ) : (
          <>
            <strong>Install ReyaGo</strong> on this device for faster, full-screen access — even with a spotty connection.
          </>
        )}
      </div>
      {!showIosHelp && (
        <button
          onClick={handleInstall}
          style={{
            background: 'white',
            color: '#4A2D4F',
            border: 'none',
            borderRadius: 8,
            padding: '6px 12px',
            fontWeight: 600,
            fontSize: 13,
            cursor: 'pointer',
          }}
        >
          Install
        </button>
      )}
      <button
        onClick={dismiss}
        aria-label="Dismiss"
        style={{
          background: 'transparent',
          color: 'white',
          border: 'none',
          fontSize: 18,
          lineHeight: 1,
          cursor: 'pointer',
          padding: '0 4px',
        }}
      >
        ×
      </button>
    </div>
  );
}
